import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'

/**
 * ContactTableRow - Single contact row in admin contacts table
 * 
 * @atomic - Single responsibility: render contact row
 * @idempotent - Pure component with React.memo
 * @modular - Reusable across admin sections
 */
const ContactTableRow = memo(function ContactTableRow({
  contact,
  isSelected,
  onSelect,
  onStatusUpdate,
  getVendorIntentLabel,
  getStatusBadge
}) {
  const navigate = useNavigate()

  const handleRowClick = (e) => {
    // Don't navigate if clicking buttons or checkbox
    if (e.target.closest('button') || e.target.closest('input[type="checkbox"]')) return
    navigate(`/admin/contacts/${contact.id}`)
  }

  const handleCheckboxClick = (e) => {
    e.stopPropagation()
  }

  return (
    <tr 
      onClick={handleRowClick}
      className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
    >
      {/* Checkbox */}
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap" onClick={handleCheckboxClick}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(contact.id)}
          className="rounded border-gray-300 text-primary focus:ring-primary"
        />
      </td>

      {/* Contact Info */}
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col">
          <div className="text-sm font-medium text-gray-900">{contact.name}</div>
          <div className="text-sm text-gray-500">{contact.email}</div>
        </div>
      </td>

      {/* Subject & Message Preview */}
      <td className="px-4 lg:px-6 py-4">
        <div className="text-sm text-gray-900">{contact.subject || '(No subject)'}</div>
        <div className="text-sm text-gray-500 truncate max-w-xs">
          {contact.message?.substring(0, 60)}...
        </div>
      </td>

      {/* Type & Vendor Intent */}
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        {contact.isVendor ? (
          <div className="flex flex-col">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Vendor
            </span>
            {contact.vendorIntent && (
              <span className="text-xs text-gray-500 mt-1">
                {getVendorIntentLabel(contact.vendorIntent)}
              </span>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            General
          </span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(contact.status)}`}>
          {contact.status}
        </span>
      </td>

      {/* Date */}
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(contact.createdAt).toLocaleDateString()}
      </td>

      {/* Actions */}
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex gap-2">
          {contact.status === 'pending' && (
            <>
              <button
                onClick={() => onStatusUpdate(contact.id, 'reviewed')}
                className="text-blue-600 hover:text-blue-900"
                title="Mark as Reviewed"
              >
                <CheckCircleIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => onStatusUpdate(contact.id, 'resolved', 'Completed')}
                className="text-green-600 hover:text-green-900"
                title="Mark as Resolved"
              >
                <CheckCircleIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => onStatusUpdate(contact.id, 'spam', 'Marked as spam')}
                className="text-red-600 hover:text-red-900"
                title="Mark as Spam"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            </>
          )}
          {contact.status === 'reviewed' && (
            <button
              onClick={() => onStatusUpdate(contact.id, 'resolved', 'Completed')}
              className="text-green-600 hover:text-green-900"
              title="Mark as Resolved"
            >
              <CheckCircleIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
})

export default ContactTableRow
